import { defineMessages } from 'react-intl';

import { PandoraTypes } from '../../constants/SourceTypes';

export const DOMAIN_NAME = 'playback' as const;

export const PlaybackKeys = Object.freeze({
  HAS_PLAYBACK_STARTED_IN_SESSION: 'HAS_PLAYBACK_STARTED_IN_SESSION',
  SHOULD_AUTO_START: 'SHOULD_AUTO_START',
  SOURCE_STARTING: 'SOURCE_STARTING',
} as const);

export const ActionKeys = Object.freeze({
  ADD_REPLAY_TRACK_TO_PLAYLIST: 'GLOBAL_ADD_REPLAY_TRACK_TO_PLAYLIST',
  ADVANCE_PLAYLIST: 'GLOBAL_ADVANCE_PLAYLIST',
  CHECK_IS_PLAYABLE_SOURCE: 'GLOBAL_CHECK_IS_PLAYABLE_SOURCE',
  CLOSE_REWARDS_COACHMARK: 'GLOBAL_CLOSE_REWARDS_COACHMARK',
  HANDLE_ARTIST_AUDIO_MESSAGE_CTA: 'HANDLE_ARTIST_AUDIO_MESSAGE_CTA',
  INSERT_NEW_SIM_STREAM_VIOLATION: 'GLOBAL_INSERT_NEW_SIM_STREAM_VIOLATION',
  LOAD_PREFETCHED_TRACKS: 'LOAD_PREFETCHED_TRACKS',
  LOAD_STATION_RECOMMENDATIONS: 'LOAD_STATION_RECOMMENDATIONS',
  PLAY_REWARDS_AD: 'GLOBAL_PLAY_REWARDS_AD',
  PLAY_TRACK_FROM_STATION: 'GLOBAL_PLAY_TRACK_FROM_STATION',
  PROMPT_STREAM_VIOLATION: 'GLOBAL_PROMPT_STREAM_VIOLATION',
  RESET_PLAYBACK_STARTED: 'RESET_PLAYBACK_STARTED',
  SET_ARE_VOICE_TRACKS_ENABLED: 'SET_ARE_VOICE_TRACKS_ENABLED',
  SET_AUTO_START_ENABLED: 'SET_AUTO_START_ENABLED',
  SET_CURRENT_TRACK_IN_PLAYLIST: 'GLOBAL_SET_CURRENT_TRACK_IN_PLAYLIST',
  START_INITIAL_PLAYBACK: 'START_INITIAL_PLAYBACK',
  START_NEXT_NON_SHUFFLE_STATION: 'GLOBAL_START_NEXT_NON_SHUFFLE_STATION',
  START_VALUE_EXCHANGE: 'GLOBAL_START_VALUE_EXCHANGE',
  STATION_SET_PLAYLIST: 'GLOBAL_STATION_SET_PLAYLIST',
  STATION_TRACK_RESUMED_FROM_SOURCE: 'STATION_TRACK_RESUMED_FROM_SOURCE',
  UPDATE_SERVER_PLAYBACK_PAUSED: 'UPDATE_SERVER_PLAYBACK_PAUSED',
  UPDATE_STATION: 'GLOBAL_UPDATE_STATION',
} as const);

export const RECENTLY_PLAYED_TIME_WINDOW_SEC = 2;

export const tunerControlMessages = defineMessages({
  aboutTheArtist: {
    id: 't.aboutTheArtist',
    defaultMessage: 'About the Artist',
  },
  buffering: {
    id: 't.buffering',
    defaultMessage: 'BUFFERING...',
  },
  customTrackInSessionHistory: {
    id: 't.customTrackInSessionHistory',
    defaultMessage: 'Enjoy this custom track.',
  },
  muteVolume: {
    id: 't.muteVolume',
    defaultMessage: 'Mute volume',
  },
  play: {
    id: 't.play',
    defaultMessage: 'Play',
  },
  playProgress: {
    id: 't.playProgress',
    defaultMessage:
      'Playing {h, plural, =0 {} =1 {# Hour} other {# Hours}} {m, plural, =0 {} =1 {# Minute} other {# Minutes}} {s, plural, =0 {} =1 {# Second} other {# Seconds}}',
  },
  repeatAll: {
    id: 't.repeatAll',
    defaultMessage: 'Repeat all',
  },
  repeatOff: {
    id: 't.repeatOff',
    defaultMessage: 'Repeat off',
  },
  repeatOne: {
    id: 't.repeatOne',
    defaultMessage: 'Repeat one',
  },
  shuffle: {
    id: 't.shuffle',
    defaultMessage: 'Shuffle',
  },
  skip15Back: {
    id: 't.skip15Back',
    defaultMessage: 'Rewind 15 seconds',
  },
  skip15Forward: {
    id: 't.skip15Forward',
    defaultMessage: 'Fast forward 15 seconds',
  },
  skipBackwards: {
    id: 't.skipBackwards',
    defaultMessage: 'Skip backwards',
  },
  skipForwards: {
    id: 't.skipForwards',
    defaultMessage: 'Skip forwards',
  },
  thumbDownSong: {
    id: 't.thumbDownSong',
    defaultMessage: 'Thumb Down this song',
  },
  thumbUpSong: {
    id: 't.thumbUpSong',
    defaultMessage: 'Thumb Up this song',
  },
  volumePercent: {
    id: 't.volumePercent',
    defaultMessage: 'Volume {percentage, number} percent',
  },
});

/**
 * @module constants/Globals/Playback
 */

export const PlaybackMessages = defineMessages({
  adSupportedSkipDailyLimitReachedBody: {
    id: 't.adSupportedSkipDailyLimitReachedBody',
    defaultMessage:
      'Our music licenses limit the number of tracks you can skip. Upgrade to Pandora Plus to get more.',
  },
  adSupportedSkipDailyLimitReachedButton: {
    id: 't.adSupportedSkipDailyLimitReachedButton',
    defaultMessage: 'Upgrade',
  },
  adSupportedSkipDailyLimitReachedTitle: {
    id: 't.adSupportedSkipDailyLimitReachedTitle',
    defaultMessage: "You've hit your skip limit.",
  },
  adSupportedThumbDownDailySkipLimitReached: {
    id: 't.adSupportedThumbDownDailySkipLimitReached',
    defaultMessage:
      "Thanks for your input. When this track is over, we'll never play it again on this station.",
  },
  anonStationLimitReached: {
    id: 't.anonStationLimitedReached',
    defaultMessage: 'Your station limit has been reached. Register now for free to create more.',
  },
  audioMessagesDisabled: {
    id: 't.unavailableVoiceTrack',
    defaultMessage: 'Audio messages are disabled in shuffle mode.',
  },
  autoplay: {
    id: 't.name',
    defaultMessage: 'AutoPlay',
  },
  bandwidthInsufficient: {
    id: 't.bandwidthInsufficient',
    defaultMessage:
      'We are experiencing trouble playing audio. If problems persist check your internet connection and refresh the browser.',
  },
  blockedAudioErrorBody: {
    id: 't.blockedAudioErrorBody',
    defaultMessage:
      "We can't load media right now. This is commonly caused by overzealous ad blockers or privacy extensions.  Please check that your browser extensions are not blocking our audio and refresh the page. If you continue to see this error you should contact support.",
  },
  blockedAudioErrorRefresh: {
    id: 't.blockedAudioErrorRefresh',
    defaultMessage: 'Refresh',
  },
  blockedAudioErrorSupport: {
    id: 't.blockedAudioErrorSupport',
    defaultMessage: 'Go To Support',
  },
  blockedAudioErrorTitle: {
    id: 't.blockedAudioErrorTitle',
    defaultMessage: "We're having trouble playing audio on this device.",
  },
  episodeDescription: {
    id: 't.episodeDescription',
    defaultMessage: 'Episode Description',
  },
  expiredStation: {
    id: 't.expiredStation',
    defaultMessage: 'This station is no longer available. Please choose another.',
  },
  firstTimeThumbDownEducation: {
    id: 't.firstTimeThumbUpEducation',
    defaultMessage:
      'Not the right song? Because you gave it a thumbs down, we’ll avoid playing more songs like it on this station.',
  },
  firstTimeThumbUpEducation: {
    id: 't.firstTimeThumbUpEducation',
    defaultMessage:
      'Got it! You gave this song a thumbs up, so we’ll play more like it on this station.',
  },
  havingTroubleStartingPlayback: {
    id: 't.havingTroubleStartingPlayback',
    defaultMessage:
      'We are having trouble starting playback, please refresh and if errors persist please contact support.',
  },
  listenerNotAuthorizedForStation: {
    id: 't.listenerNotAuthorizedForStation',
    defaultMessage:
      "We're sorry, we could not play music on this station. Please try another station.",
  },
  liveTrackInSessionHistory: {
    id: 't.liveTrackInSessionHistory',
    defaultMessage: 'This live stream has concluded.',
  },
  moreFromThisCategory: {
    id: 't.moreFromThisCategory',
    defaultMessage: 'More From This Category',
  },
  moreFromThisPodcast: {
    id: 't.moreFromThisPodcast',
    defaultMessage: 'More From This Podcast',
  },
  noRightsForSource: {
    id: 't.noRightsForSource',
    defaultMessage: "We don't have rights for the requested content, please try something else.",
  },
  pandoraPlusSkipDailyLimitReachedBody: {
    id: 't.pandoraPlusSkipDailyLimitReachedBody',
    defaultMessage:
      'Our music licenses limit the number of tracks you can skip. Upgrade to Pandora Plus to get more.',
  },
  pandoraPlusSkipDailyLimitReachedButton: {
    id: 't.pandoraPlusSkipDailyLimitReachedButton',
    defaultMessage: 'Upgrade',
  },
  pandoraPlusSkipDailyLimitReachedTitle: {
    id: 't.pandoraPlusSkipDailyLimitReachedTitle',
    defaultMessage: "You've hit your skip limit.",
  },
  pandoraPlusThumbDownDailySkipLimitReached: {
    id: 't.pandoraPlusThumbDownDailySkipLimitReached',
    defaultMessage:
      "Thanks for your input. When this track is over, we'll never play it again on this station.",
  },
  playlistEnded: {
    id: 't.playlistEnded',
    defaultMessage:
      "We're sorry, but we can't find any more music to play on your station right now. Try switching stations.",
  },
  playlistHasNoTracks: {
    id: 't.playlistHasNoTracks',
    defaultMessage: 'You have not added tracks to this playlist.',
  },
  radioOnlyAlbum: {
    id: 't.radioOnlyAlbum',
    defaultMessage: 'The songs on this album are only currently available for radio playback.',
  },
  radioOnlyPlaylist: {
    id: 't.radioOnlyPlaylist',
    defaultMessage: 'The songs on this playlist are only currently available for radio playback.',
  },
  radioOnlySong: {
    id: 't.radioOnlySong',
    defaultMessage: 'This song is only currently available for radio playback.',
  },
  removeStationFromCollectionConfirm: {
    id: 't.removeStationFromCollectionConfirm',
    defaultMessage: '"{name}" has been removed from your collection',
  },
  removeStationUndo: {
    id: 't.removeStationUndo',
    defaultMessage: 'Undo',
  },
  replayNotAvailable: {
    id: 't.replayNotAvailable',
    defaultMessage: 'Replay not available.',
  },
  replayNotAvailableLicensing: {
    id: 't.replayNotAvailable',
    defaultMessage: 'This song can’t currently be replayed.',
  },
  seeMore: {
    id: 't.seeMore',
    defaultMessage: 'See More',
  },
  seeProfile: {
    id: 't.seeProfile',
    defaultMessage: "See {name}'s Profile",
  },
  similarArtists: {
    id: 't.similarArtists',
    defaultMessage: 'Similar Artists',
  },
  similarPodcasts: {
    id: 't.similarPodcasts',
    defaultMessage: 'Similar Podcasts',
  },
  skipLimitReached: {
    id: 't.skipLimitReached',
    defaultMessage: 'This song can’t currently be skipped right now.',
  },
  startArtistStation: {
    id: 't.startArtistStation',
    defaultMessage: 'Start Artist Station',
  },
  startStation: {
    id: 't.startStation',
    defaultMessage: 'Start Station',
  },
  startStationFail: {
    id: 't.startStationFail',
    defaultMessage: "We couldn't start the selected station. Please choose another.",
  },
  stationLimitReached: {
    id: 't.stationLimitReached',
    defaultMessage:
      'Your station limit has been reached. Please delete some stations in order to add more.',
  },
  thumbDownHourlySkipLimitReached: {
    id: 't.thumbDownHourlySkipLimitReached',
    defaultMessage:
      "Thanks for your input. When this track is over, we'll never play it again on this station",
  },
  thumbprintRadioIneligible: {
    id: 't.thumbprintRadioIneligible',
    defaultMessage:
      "Want Thumbprint Radio? We need to know you a little better. Keep creating stations and thumbing up songs you love, and we'll let you know when it's ready!",
  },
  tiredOfTrack: {
    id: 't.tiredOfTrack',
    defaultMessage: "Ok, we'll put it on the shelf for a while.",
  },
  unavailableAlbum: {
    id: 't.unavailableAlbum',
    defaultMessage: "This album isn't currently available for playback.",
  },
  unavailableArtistStation: {
    id: 't.unavailableArtistStation',
    defaultMessage: "This artist isn't currently available for station listening.",
  },
  unavailablePlaylist: {
    id: 't.unavailablePlaylist',
    defaultMessage: "The songs on this playlist aren't currently available for playback.",
  },
  unavailablePodcast: {
    id: 't.unavailablePodcast',
    defaultMessage: 'This podcast is currently unavailable.',
  },
  unavailableSong: {
    id: 't.unavailableSong',
    defaultMessage: "This song isn't currently available for playback.",
  },
  unavailableSongStation: {
    id: 't.unavailableSongStation',
    defaultMessage: "This song isn't currently available for station listening.",
  },
  weCouldNotStartSelectedStation: {
    id: 't.weCouldNotStartSelectedStation',
    defaultMessage:
      "We couldn't start the selected station. We started playing your last station instead.",
  },
});

export const voiceTrackMessages = defineMessages({
  artistAudioMessagesOff: {
    id: 't.artistAudioMessagesOff',
    defaultMessage: 'Audio messages are off',
  },
  artistAudioMessagesOn: {
    id: 't.artistAudioMessagesOn',
    defaultMessage: 'Audio messages are on',
  },
  artistAudioMessagesTurnOn: {
    id: 't.artistAudioMessagesTurnOn',
    defaultMessage: 'TURN ON',
  },
  description: {
    id: 't.shortDescription',
    defaultMessage: 'Hear from artists and curators.',
  },
  name: {
    id: 't.name',
    defaultMessage: 'Artist Audio Messages',
  },
});

export const SessionLengths = Object.freeze({
  [PandoraTypes.AUTOPLAY]: 30,
  [PandoraTypes.USER_STATION]: 30,
});

export const REPEAT_MODES = Object.freeze({
  ALL: 'REPEAT_ALL',
  NONE: 'REPEAT_NONE',
  ONE: 'REPEAT_ONE',
});

export const REPEAT_ORDER = [REPEAT_MODES.NONE, REPEAT_MODES.ALL, REPEAT_MODES.ONE];

export const PlaylistKeys = Object.freeze({
  ARE_VOICE_TRACKS_ENABLED: 'ARE_VOICE_TRACKS_ENABLED',
  CURRENT_INDEX: 'PLAYLIST_CURRENT_INDEX',
  CURRENT_PLAYLIST: 'PLAYLIST_CURRENT_PLAYLIST',
  CURRENT_TOKEN: 'PLAYLIST_CURRENT_TOKEN',
  CURRENT_TRACK_DURATION: 'PLAYLIST_TRACK_DURATION',
  CURRENT_TRACK_TIME: 'PLAYLIST_TRACK_TIME',
  LAST_PLAYED: 'PLAYLIST_LAST_PLAYED',
  PLAYLIST_SOURCE_TYPE: 'PLAYLIST_SOURCE_TYPE',
  PLAYLIST_TRACK_MODEL_TYPE: 'PLAYLIST_TRACK_MODEL_TYPE',
  PLAYLISTS: 'PLAYLIST_PLAYLISTS',
  PREFETCHED_TRACKS: 'PLAYLIST_PREFETCHED_TRACKS',
  TRACKS: 'PLAYLIST_TRACKS',
});
